import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import classNames from 'classnames';
import _ from 'lodash';
import s from './Image.module.scss';

const Image = ({
    src = '',
    width = null,
    height = null,
    alt = '',
    caption = '',
    focal = {},
    srcset = [],
    sizes = [],
    minAreaRatio = null,
    style = {},
    useCover = false,
    className = '',
    lazyLoad = true,
    priority = false,
    asImg = false,
    overrideSrc = true,
    setLoaded = () => {},
}) => {
    const [useObjectFit, setUseObjectFit] = useState(true);

    useEffect(() => {
        // Fallback for object fit, e.g. ie11
        const styles = document.documentElement.style;
        if('objectFit' in styles === false) {
            setUseObjectFit(false);
        }
    }, []);

    const position = !_.isEmpty(focal) ? `${focal.x} ${focal.y}` : 'center center';

    const classes = classNames(
        [s['Image']],
        {[s['Image--Fallback']]: !useObjectFit},
        {[s['Image--ObjectFit']]: useObjectFit},
        {[s['Image--Cover']]: useCover},
        {[s['Image--Contain']]: !useCover},
        className,
    );

    if(!useObjectFit) {
        return (
            <div
                className={classes}
                style={{
                    ...style,
                    backgroundImage: `url('${src}')`,
                    backgroundPosition: position,
                }}
                title={caption}
            >
                <img
                    className={s['Image__LoadEvent']}
                    src={src}
                    alt=""
                    onLoad={() => setLoaded()}
                />
            </div>
        );
    }

    if(asImg) {
        return (
            <img
                className={classes}
                width={width}
                height={height}
                src={src}
                alt={alt}
                title={caption}
                srcSet={srcset.join(', ')}
                sizes={sizes.join(', ')}
                style={{
                    ...style,
                    objectPosition: position,
                }}
                loading={lazyLoad ? 'lazy' : null}
                onLoad={() => setLoaded(true)}
            />
        );
    }

    // Triggers for each srcset generated by NextJS
    // Pass minimum aspect ratio so we can define a different image width if much wider than anticipated
    // TODO: Maybe only change when ratio is way off
    const loader = ({
        src: loaderSrc,
        width: loaderWidth,
        quality: loaderQuality = 75,
    }) => {
        // Device sizes set in NextJS config or using default, these are the only allowed sizes
        const srcSetSizes = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];
        const getNextHigherValue = (val) => {
            const index = _.sortedIndex(srcSetSizes, val);
            return srcSetSizes[index] !== undefined ? srcSetSizes[index] : 3840;
        };

        const loaderUrl = `/_next/image/?url=${encodeURIComponent(loaderSrc)}`;
        // Get aspect ratio from original image, check if the loader width is wide enough
        const ratio = height / width;

        // If the image ratio is lower than the area ratio and is landscape image
        // Make sure the image loaded is high enough to fill the area without scaling up
        if(ratio < minAreaRatio && height < width) {
            const loaderHeight = loaderWidth * minAreaRatio;
            const newWidth = parseInt(loaderHeight / ratio);
            const srcSetWidth = getNextHigherValue(newWidth);
            return `${loaderUrl}&w=${srcSetWidth}&q=${loaderQuality}`;
        }

        return `${loaderUrl}&w=${loaderWidth}&q=${loaderQuality}`;
    };

    return (
        <NextImage
            className={classes}
            src={src}
            overrideSrc={overrideSrc ? src : undefined} // Override src to keep original as src
            alt={alt}
            width={useCover || !width ? undefined : width}
            height={useCover || !height ? undefined : height}
            title={caption}
            loader={minAreaRatio ? loader : undefined}
            sizes={sizes.join(', ')}
            loading={lazyLoad ? 'lazy' : 'eager'}
            priority={priority}
            fill={useCover}
            onLoad={() => setLoaded(true)}
            style={{
                ...style,
                objectPosition: position,
            }}
        />
    );
};

Image.propTypes = {
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string,
    caption: PropTypes.string,
    focal: PropTypes.object,
    srcset: PropTypes.array,
    sizes: PropTypes.array,
    minAreaRatio: PropTypes.number,
    style: PropTypes.object,
    useCover: PropTypes.bool,
    className: PropTypes.string,
    lazyLoad: PropTypes.bool,
    priority: PropTypes.bool,
    overrideSrc: PropTypes.bool,
    asImg: PropTypes.bool,
    setLoaded: PropTypes.func,
};

export default Image;
